import { yupResolver } from '@hookform/resolvers/yup';
import { alwaysRefetchApiQueryOptions, emptyBeneficiaryState } from 'BreetHelpers';
import { useFormStage, useUserCurrencyProps, useUserWalletApi, useWithdrawRequestContext } from 'BreetHooks';
import { selectMeta, selectSelectedBeneficiary, useAppSelector, useGetBeneficiariesQuery } from 'BreetRedux';
import { type ReactNode, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';

import {
	CreateWithdrawRequestFormSchema,
	type FIATWITHDRAWREQUESTSTAGETYPE,
	requestPaymentStageTitle,
} from '@/components/DialogWithdrawRequest/DialogWithdrawRequest.helpers';

import { FiatWithdrawRequestContext } from '../context/FormContext';

export const FiatWithdrawRequestProvider = ({ children }: { children?: ReactNode }) => {
	const { selectedWallet } = useWithdrawRequestContext();

	const currencyProps = useUserCurrencyProps();

	const walletCurrencyCode = selectedWallet?.currency ?? currencyProps?.code;

	const userWalletProps = useUserWalletApi(walletCurrencyCode);

	const { data: beneficiariesData } = useGetBeneficiariesQuery(undefined, alwaysRefetchApiQueryOptions);

	const meta = useAppSelector(selectMeta);

	const beneficiaries = useMemo(() => beneficiariesData?.data ?? [], [beneficiariesData]);

	const beneficiary = useAppSelector(selectSelectedBeneficiary);

	const defaultNarration = beneficiaries.length > 1 ? '' : (beneficiary?.narration ?? '');

	const defaultSelectedBeneficiary =
		beneficiaries.length > 1
			? emptyBeneficiaryState
			: {
					accountName: beneficiary?.accountName ?? '',
					accountNumber: beneficiary?.accountNumber ?? '',
					bankName: beneficiary?.bankName ?? '',
					id: beneficiary?._id ?? '',
				};

	const withdrawRequestHookForm = useForm({
		resolver: yupResolver(CreateWithdrawRequestFormSchema),
		defaultValues: {
			amount: '',
			selectedBeneficiary: defaultSelectedBeneficiary,
			narration: defaultNarration,
		},
		mode: 'onChange',
	});

	useEffect(() => {
		withdrawRequestHookForm.setValue('selectedBeneficiary', defaultSelectedBeneficiary);
		withdrawRequestHookForm.setValue('narration', defaultNarration);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [defaultSelectedBeneficiary.id]);

	const formStage = useFormStage<FIATWITHDRAWREQUESTSTAGETYPE>(
		'withdraw-funds',
		['withdraw-funds', 'add-beneficiary', 'withdraw-details', 'manage-beneficiaries'],
		requestPaymentStageTitle
	);

	const providerValue = useMemo(
		() => ({
			userWalletProps,
			meta,
			withdrawRequestHookForm,
			formStage,
			beneficiaries,
		}),
		[meta, userWalletProps, withdrawRequestHookForm, formStage, beneficiaries]
	);

	return <FiatWithdrawRequestContext.Provider value={providerValue}>{children}</FiatWithdrawRequestContext.Provider>;
};
